import React, { FC, useCallback, useEffect } from "react";

import { useForm } from "antd/es/form/Form";
import { defineMessages, useIntl } from "react-intl";

import { IWizardStep, StandardStep } from "@app/component/wizard";
import AddCardWizardState from "@app/scene/dashboard/components/addEditCard/AddCardWizard/AddCardWizardState";
import EditCardForm from "@app/scene/dashboard/components/addEditCard/components/EditCardForm";
import ScopeTag from "@app/scene/dashboard/components/ScopeTag";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import useAnalytics from "@app/util/contexts/useAnalytics";

import commonStyles from "../CommonStep.module.scss";

const messages = defineMessages({
  back: {
    id: "dashboard.addCardWizard.configureStep.backButton",
    defaultMessage: "Go Back",
    description: "Back button label in Add Card Wizard on the Dashboard page",
  },
  finish: {
    id: "dashboard.addCardWizard.configureStep.nextButton",
    defaultMessage: "Add Chart",
    description: "Finish button label in Add Card Wizard on the Dashboard page",
  },
  title: {
    id: "dashboard.addCardWizard.configureStep.title",
    defaultMessage: "Add Chart: Select Type {scope}",
    description: "Select Type step title in Add Card Wizard on the Dashboard page",
  },
});

export interface ConfigureCardStepProps extends IWizardStep<AddCardWizardState> {}

const ConfigureCardStep: FC<ConfigureCardStepProps> = ({
  onFinish,
  onGoForward, // not used but needed to exclude from ...props
  onSetState,
  state,
  ...props
}) => {
  const analytics = useAnalytics();
  useEffect(() => {
    analytics.using(["Configure Card step"]);
  }, [analytics]);

  const intl = useIntl();

  const [form] = useForm<CardConfiguration<any, any, any>>();

  const handleSubmit = useCallback(
    (configuration: CardConfiguration<any, any, any>) => {
      onSetState({ configuration });
      onFinish();
    },
    [onSetState, onFinish]
  );

  return (
    <StandardStep
      backButtonText={intl.formatMessage(messages.back)}
      nextButtonText={intl.formatMessage(messages.finish)}
      onGoForward={form.submit}
      title={intl.formatMessage(messages.title, { scope: <ScopeTag scope={state.scope} /> })}
      {...props}
    >
      <EditCardForm
        className={commonStyles.container}
        configuration={state.configuration!}
        form={form}
        onSubmit={handleSubmit}
        scope={state.scope}
        teamGoal={undefined} // TODO: should it always be disabled?
        type={state.cardType!}
      />
    </StandardStep>
  );
};

export default ConfigureCardStep;
